// This file is part of ecbyts/ECBT (Environmental & Occupational Core Byte Tools).
// Copyright (C) 2026 Calvin Stefan Iost
// Licensed under the GNU Affero General Public License v3.0 (AGPL-3.0-only).

export const ONLINE_PORTABLE_EDUCATION = Object.freeze({
    onlineAccess: 'Access is checked by the service and can be revoked for future online reads.',
    portableKey: 'A portable ECBT key is an offline copy; revocation cannot recover copies already exported.',
    nonConfidential: ['A', 'B', 'C'],
    encrypted: ['D'],
    limitation: 'Online revocation does not delete an offline copy already received by an authorized person.',
});

export const P1_EXTENSION_REVALIDATION = Object.freeze([
    { extension: 'thematic', ownerApproval: 'pending', requiredSpine: ['persona', 'capability', 'artifact', 'error', 'recovery', 'owner'] },
    { extension: 'governance', ownerApproval: 'pending', requiredSpine: ['persona', 'capability', 'artifact', 'error', 'recovery', 'owner'] },
    { extension: 'ai', ownerApproval: 'pending', requiredSpine: ['persona', 'capability', 'artifact', 'error', 'recovery', 'owner'] },
]);

const TELEMETRY_KEYS = new Set([
    'tour_id', 'status', 'persona_category', 'capability_id', 'artifact_kind', 'recovery_category',
    'step_index', 'steps_total', 'steps_shown', 'steps_failed', 'evidence_failed', 'reason_category',
]);
const SAFE_CATEGORY = /^[a-z0-9][a-z0-9_-]{0,63}$/;

export function redactAgencyTourTelemetry(metadata: Record<string, unknown>): Record<string, string | number> {
    const result: Record<string, string | number> = {};
    for (const [key, value] of Object.entries(metadata ?? {})) {
        if (!TELEMETRY_KEYS.has(key)) continue;
        if (typeof value === 'number' && Number.isFinite(value)) result[key] = value;
        else if (typeof value === 'string' && SAFE_CATEGORY.test(value)) result[key] = value;
    }
    return result;
}

export function buildFilteredSnapshotPractice(input: {
    rows: Array<Record<string, unknown>>;
    allowedFields: string[];
    redactFields: string[];
}): { stageOrder: readonly string[]; rows: Array<Record<string, unknown>>; removedFields: string[]; safeToEncrypt: boolean } {
    const allowed = new Set(input.allowedFields);
    const redacted = new Set(input.redactFields);
    const removed = new Set<string>();
    const rows = input.rows.map((row) => Object.fromEntries(Object.entries(row).flatMap(([key, value]) => {
        if (!allowed.has(key) || redacted.has(key)) {
            removed.add(key);
            return [];
        }
        return [[key, value]];
    })));
    return {
        stageOrder: Object.freeze(['filter', 'redact', 'review', 'encrypt']),
        rows,
        removedFields: [...removed].sort(),
        safeToEncrypt: rows.length > 0 && rows.every((row) => Object.keys(row).length > 0),
    };
}

export function validateP1TourSpine(input: Record<string, unknown>): string[] {
    const errors: string[] = [];
    for (const field of ['personaCategory', 'capabilityId', 'artifactKind', 'errorScenario', 'recoveryOutcome', 'ownerRole']) {
        if (!String(input[field] ?? '').trim()) errors.push(`${field}-required`);
    }
    if (input.ownerApproval !== 'approved') errors.push('owner-approval-required');
    return errors;
}
